import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Loader2, Check, Sparkles, Map, Plus, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo75MapaDecision() {
    const [reglas, setReglas] = useState([
        { id: crypto.randomUUID(), situacion: '', senal: '', accion: '' },
        { id: crypto.randomUUID(), situacion: '', senal: '', accion: '' },
        { id: crypto.randomUUID(), situacion: '', senal: '', accion: '' },
    ]);
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [usabilidad, setUsabilidad] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/mapa_decision`, { withCredentials: true });
                if (data.data?.reglas?.length >= 3) setReglas(data.data.reglas.map(r => ({ ...r, id: r.id || crypto.randomUUID() })));
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'mapa_decision', data: { reglas } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [reglas]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [reglas, saveDraft, loadingDraft, readOnly]);

    const updateRegla = (index, field, value) => {
        const updated = [...reglas];
        updated[index] = { ...updated[index], [field]: value };
        setReglas(updated);
    };

    const addRegla = () => setReglas([...reglas, { id: crypto.randomUUID(), situacion: '', senal: '', accion: '' }]);
    const removeRegla = (index) => { if (reglas.length > 3) setReglas(reglas.filter((_, i) => i !== index)); };

    const validRules = reglas.filter(r => r.situacion.trim().length > 0 && r.senal.trim().length > 0 && r.accion.trim().length > 0);
    const canSubmit = !readOnly && (validRules.length >= 3);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/mapa-decision/submit`, { reglas }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setUsabilidad(data.usabilidad);
            setSubmitted(true);
            toast.success('¡Capítulo 7 completado!');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#7A9BA8] to-[#3E5F6E] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Map className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="mapa-decision-success-title">¡Capítulo 7 completado!</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Tu mapa de decisión está operativo</p>
                    {usabilidad && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Usabilidad: {usabilidad}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#7A9BA8] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#7A9BA8] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="mapa_decision" chapter={7} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-mapa-decision">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#7A9BA8] uppercase tracking-wide">Cap. 7</span><span className="text-xs text-[#7A8A93]">7.5</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Mapa de decisión</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="px-3 py-1 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-xs font-medium">{validRules.length} reglas válidas</div>
                            {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                        </div>
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#7A9BA8] to-[#3E5F6E] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Map className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Construye tu mapa operativo</CardTitle><p className="text-xs sm:text-sm text-white/70">Mínimo 3 reglas: SI situación + señal → ENTONCES acción</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-4">
                        {reglas.map((regla, index) => {
                            const isValid = regla.situacion.trim().length > 0 && regla.senal.trim().length > 0 && regla.accion.trim().length > 0;
                            return (
                                <div key={regla.id} className={`p-4 rounded-xl border ${isValid ? 'bg-[#5FAF9D]/5 border-[#5FAF9D]/30' : 'bg-white border-[#DCE3E8]'}`} data-testid={`regla-entry-${index}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-semibold text-[#3E5F6E]">Regla {index + 1}</span>
                                        {reglas.length > 3 && <button onClick={() => removeRegla(index)} className="text-[#D07F7E] hover:text-[#C06060]"><Trash2 className="h-4 w-4" /></button>}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div><Label className="text-xs text-[#7A8A93]">Situación</Label><Input value={regla.situacion} onChange={e => updateRegla(index, 'situacion', e.target.value)} placeholder="¿Cuándo?" className="border-[#DCE3E8] rounded-lg text-sm mt-1" /></div>
                                        <div><Label className="text-xs text-[#7A8A93]">Señal</Label><Input value={regla.senal} onChange={e => updateRegla(index, 'senal', e.target.value)} placeholder="¿Qué noto?" className="border-[#DCE3E8] rounded-lg text-sm mt-1" /></div>
                                        <div><Label className="text-xs text-[#7A8A93]">Acción</Label><Input value={regla.accion} onChange={e => updateRegla(index, 'accion', e.target.value)} placeholder="¿Qué hago?" className="border-[#DCE3E8] rounded-lg text-sm mt-1" /></div>
                                    </div>
                                </div>
                            );
                        })}
                        <Button variant="outline" onClick={addRegla} className="w-full border-dashed border-[#DCE3E8] text-[#7A8A93] hover:text-[#3E5F6E]" data-testid="add-regla-btn"><Plus className="h-4 w-4 mr-2" />Agregar regla</Button>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-mapa-decision">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar capítulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
